import React, { createContext, useEffect, useState, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { AuthError } from '@supabase/supabase-js';
import {
  AuthStateMachine,
  AuthEventType,
  AuthPublicState,
  User,
  UserProfile,
  isTokenExpired,
} from '../lib/auth-state-machine';
import { configureApiAuth } from '../services/api';
import { queryClient } from '../lib/queryClient';
import { setAnalyticsUserId } from '../utils/analytics';

interface AuthContextType {
  // Public state from machine
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthPublicState['user'];
  profile: UserProfile | null;
  error: Error | null;

  // Auth operations
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: (scope?: 'global' | 'local') => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  verifyPasswordResetOtp: (
    email: string,
    token: string
  ) => Promise<{ error: AuthError | null; session?: unknown }>;
  updatePassword: (newPassword: string) => Promise<{ error: AuthError | null }>;
  getAuthHeaders: () => Record<string, string>;

  // Debug utilities
  getDebugInfo: () => {
    state: string;
    isAuthenticated: boolean;
    user: User | null;
    profile: UserProfile | null;
  };
}

const AuthContextV2 = createContext<AuthContextType | undefined>(undefined);

// Export context for the hook. eslint-plugin-react-refresh >= 0.5 flags a context
// exported next to a component (the file loses Fast Refresh and remounts on edit);
// moving it to its own module is a small refactor left for a follow-up, not a
// toolchain PR (#1395).
// eslint-disable-next-line react-refresh/only-export-components
export { AuthContextV2 };

// Re-export the hook from its new location for backward compatibility
// eslint-disable-next-line react-refresh/only-export-components
export { useAuthV2 } from '../hooks/useAuthV2';

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProviderV2: React.FC<AuthProviderProps> = ({ children }) => {
  const machineRef = useRef<AuthStateMachine | null>(null);
  const wasAuthenticatedRef = useRef(false);
  const [publicState, setPublicState] = useState<AuthPublicState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    profile: null,
    error: null,
  });

  // Initialize the state machine
  useEffect(() => {
    if (!machineRef.current) {
      machineRef.current = new AuthStateMachine();

      // Expose for debugging
      if (typeof window !== 'undefined') {
        (window as Window & { __authMachine?: AuthStateMachine }).__authMachine =
          machineRef.current;
      }

      // Configure API with auth token provider and logout callback
      configureApiAuth(
        () => {
          const token = machineRef.current?.getSession()?.access_token || null;

          // Check if token is expired before using it
          if (token && isTokenExpired(token)) {
            console.warn('JWT token is expired, triggering logout');
            // Trigger logout to get fresh token
            if (machineRef.current) {
              machineRef.current.send({ type: AuthEventType.LOGOUT });
            }
            return null;
          }

          return token;
        },
        () => {
          // Handle logout from API 401 errors
          if (machineRef.current) {
            machineRef.current.send({ type: AuthEventType.LOGOUT });
          }
        }
      );

      // Subscribe to state changes
      const unsubscribe = machineRef.current.subscribe((newState) => {
        setPublicState(newState);

        // Keep Sentry + GA4 user context in sync with auth state.
        // GA4 user_id = Supabase UUID (non-PII; resolves to email server-side only).
        if (newState.isAuthenticated && newState.user) {
          Sentry.setUser({ id: newState.user.id, email: newState.user.email });
          setAnalyticsUserId(newState.user.id);
        } else {
          Sentry.setUser(null);
          setAnalyticsUserId(null);
          // Drop all cached server state only on an actual logout (authenticated
          // -> unauthenticated), not on the initial unauthenticated mount, so a
          // different user signing in next can't transiently see the previous
          // user's data (STD-015).
          if (wasAuthenticatedRef.current) {
            queryClient.clear();
          }
        }
        wasAuthenticatedRef.current = newState.isAuthenticated;
      });

      return () => {
        unsubscribe();
        if (machineRef.current) {
          machineRef.current.destroy();
        }
      };
    }
  }, []);

  // Auth operations
  const signIn = async (email: string, password: string) => {
    if (!machineRef.current) {
      return { error: new Error('Auth machine not initialized') as AuthError };
    }
    return machineRef.current.login(email, password);
  };

  const signOut = async (scope?: 'global' | 'local') => {
    if (!machineRef.current) {
      console.warn('Auth machine not initialized for signOut');
      return;
    }

    await machineRef.current.send({ type: AuthEventType.LOGOUT, payload: { scope } });
  };

  const resetPassword = async (email: string) => {
    if (!machineRef.current) {
      return { error: new Error('Auth machine not initialized') as AuthError };
    }
    return machineRef.current.resetPassword(email);
  };

  const verifyPasswordResetOtp = async (email: string, token: string) => {
    if (!machineRef.current) {
      return {
        error: new Error('Auth machine not initialized') as AuthError,
        session: null,
      };
    }
    return machineRef.current.verifyPasswordResetOtp(email, token);
  };

  const updatePassword = async (newPassword: string) => {
    if (!machineRef.current) {
      return { error: new Error('Auth machine not initialized') as AuthError };
    }
    return machineRef.current.updatePassword(newPassword);
  };

  const getAuthHeaders = (): Record<string, string> => {
    if (!machineRef.current) {
      return {};
    }
    return machineRef.current.getAuthHeaders();
  };

  const getDebugInfo = (): {
    state: string;
    isAuthenticated: boolean;
    user: User | null;
    profile: UserProfile | null;
  } => {
    if (!machineRef.current) {
      return {
        state: 'uninitialized',
        isAuthenticated: false,
        user: null,
        profile: null,
      };
    }
    return machineRef.current.getDebugInfo();
  };

  const value: AuthContextType = {
    // State from machine
    isAuthenticated: publicState.isAuthenticated,
    isLoading: publicState.isLoading,
    user: publicState.user,
    profile: publicState.profile,
    error: publicState.error,

    // Operations
    signIn,
    signOut,
    resetPassword,
    verifyPasswordResetOtp,
    updatePassword,
    getAuthHeaders,
    getDebugInfo,
  };

  return <AuthContextV2.Provider value={value}>{children}</AuthContextV2.Provider>;
};
